import { useApi, useCaller } from '@/hooks/api.hook';
import { useRootSelector } from '@/hooks/selector.hook';
import {
    setCashbackDetailAction,
    setCashbackLoading,
    setListCashbackAction
} from '@/redux/slicers/cashback.slice';
import { setMessageAction } from '@/redux/slicers/api-message.slice';
import { generateUrlParams } from '@/utils/common';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

export const useCashback = () => {
    const api = useApi('');
    const caller = useCaller();
    const dispatch = useDispatch();
    const customerId = useRootSelector((state) => state.auth.customerId);

    const getAllCashback = useCallback(async () => {
        const { data, isSuccess } = await caller(
            () => api.get(`/v2/api/customer/${customerId}/cashback/overview`),
            {
                loadingKey: 'get-all-cashback',
            },
        );

        if (isSuccess) {
            dispatch(setListCashbackAction({ data }));
        }
    }, [caller, api, dispatch]);

    const getCashbackDetail = useCallback(
        async ({ accrualId, page = 1, pageSize = 10, append = false }: GetCashbackDetailParams) => {
            // Check if customerId exists before making API call
            if (!customerId) {
                dispatch(setCashbackLoading(false));
                return;
            }

            const queryParams: { [key: string]: string | undefined } = {
                page: page.toString(),
                pageSize: pageSize.toString(),
            };

            // Remove undefined values
            Object.keys(queryParams).forEach((key) => {
                if (queryParams[key] === undefined || queryParams[key] === '') {
                    delete queryParams[key];
                }
            });

            const urlParams = generateUrlParams(queryParams);

            dispatch(setCashbackLoading(true));

            const { data, isSuccess } = await caller(
                () =>
                    api.get(
                        `/v2/api/customer/${customerId}/cashback/accruals/${accrualId}?${urlParams}`,
                    ),
                {
                    loadingKey: 'get-cashback-detail',
                },
            );

            if (isSuccess && data) {
                dispatch(
                    setCashbackDetailAction({
                        accrual: data.accrual,
                        transactions: data.transactions.items,
                        pagination: {
                            Page: data.transactions.page,
                            totalPages: data.transactions.totalPages,
                            totalElements: data.transactions.totalCount,
                            Size: data.transactions.pageSize,
                        },
                        append,
                    }),
                );
            }

            dispatch(setCashbackLoading(false));
        },
        [caller, api, dispatch, customerId],
    );



    const getCashbackAccruals = useCallback(
        async (page: number = 1, pageSize: number = 10) => {
            const response = await caller(
                () => api.get(`/v2/api/customer/${customerId}/cashback/accruals?page=${page}&pageSize=${pageSize}`),
                {
                    loadingKey: 'get-cashback-accruals',
                },
            );

            return response;
        },
        [caller, api, customerId],
    );

    const getCashbackRedemptions = useCallback(
        async (page: number = 1, pageSize: number = 10) => {
            const response = await caller(
                () => api.get(`/v2/api/customer/${customerId}/cashback/redemptions?page=${page}&pageSize=${pageSize}`),
                {
                    loadingKey: 'get-cashback-redemptions',
                },
            );

            return response;
        },
        [caller, api, customerId],
    );

    const withdrawCashback = useCallback(
        async (amount: number, destinationVirtualAccountId: string, idempotencyKey: string) => {
            const response = await caller(
                () =>
                    api.post(`/v2/api/customer/${customerId}/cashback/withdraw`, {
                        amount,
                        destinationVirtualAccountId,
                    }, {
                        headers: {
                            'X-Idempotency-Key': idempotencyKey,
                        },
                    }),
                {
                    loadingKey: 'withdraw-cashback',
                },
            );

            if (response?.isSuccess) {
                dispatch(setMessageAction(['global', { type: 'success', messages: ['Cashback withdrawn successfully'] }]));
            }

            return response;
        },
        [caller, api, dispatch, customerId],
    );

    return {
        getAllCashback,
        getCashbackDetail,
        getCashbackAccruals,
        getCashbackRedemptions,
        withdrawCashback,
    };
}
